import type { OnBoardingStore } from "@/Modules/OnBoarding/Store";
import type { UserStore } from "@/Modules/User/Store";
import type { SubscriptionStore } from "@/Modules/Subscription/Store";
import type { Component } from "@/Modules/Models/Domain/OnBoarding";
import {
  StepId,
  AnswersIds,
  MinorAgeAnswers,
  TherapyType,
  ChoicesName,
  YesOrNoAnswer,
  StepIdWithChoices,
  AREA_EXTRA_THERAPIST_KNOW,
  SUBSCRIPTION_ACTIVE_THERAPIST_KNOWN,
  SUBSCRIPTION_ACTIVE_LAST_THERAPIST,
  WITHOUT_THERAPIST,
  B2B_USER,
  CUSTOM_B2B_USER,
  WELLHUB_USER,
  OTHER_LOCATION,
} from "@/Modules/Models/Domain/OnBoarding";

import useUserStore from "@/Modules/User/Store";
import useOnBoardingStore from "@/Modules/OnBoarding/Store";
import useSubscriptionStore from "@/Modules/Subscription/Store";

import { hasValue } from "@/Utils/Objects";
class SetCurrentStepActionHandler {
  constructor(
    private readonly onBoardingStore: OnBoardingStore = useOnBoardingStore(),
    private readonly userStore: UserStore = useUserStore(),
    private readonly subscriptionStore: SubscriptionStore = useSubscriptionStore()
  ) {}

  public handle(answerId: Component | null, stepId: StepId) {
    if (this.onBoardingStore.stepMap[stepId]?.length) {
      const availableSteps = this.onBoardingStore.stepMap[stepId].filter((step) => {
        //PATIENT COME BACK
        if (this.onBoardingStore.currentStep === StepId.WELCOME_AGAIN) {
          return this.getStepForWelcomeAgain(step.id);
        }

        if (answerId?.id === AnswersIds.GO_TO_LAST_THERAPIST) {
          return this.getStepGoToLastTherapist(step.id);
        }

        if (this.onBoardingStore.currentStep === StepId.REGISTER_USER_COME_BACK) {
          return this.getStepForRegisterUserComeBack(step.id);
        }

        if (this.onBoardingStore.currentStep === StepId.CHECKOUT) {
          return this.getStepForCheckout(step.id);
        }
        //PATIENT COME BACK

        //REASSIGN
        if (answerId?.id === AnswersIds.ZONE) {
          return this.getStepForZone(step.id);
        }

        if (answerId?.id === AnswersIds.REASSIGN_REASON) {
          return this.getStepForReassignReason(step.id);
        }
        //REASSIGN

        if (answerId?.id === AnswersIds.USERNAME) {
          return this.getStepForUsername(step.id);
        }

        if (answerId?.id === AnswersIds.THERAPY_TYPE) {
          return this.getStepForTherapyType(step.id);
        }

        if (answerId?.id === AnswersIds.FIRST_TIME && !this.userStore.isSSOPatient) {
          return this.onBoardingStore.answers[AnswersIds.THERAPY_TYPE] === step.id;
        }

        if (answerId?.id === AnswersIds.MAIN_AREA) {
          return this.getStepForMainArea(step.id);
        }

        if (answerId?.id === AnswersIds.SUB_AREA && answerId.choicesName === ChoicesName.COUPLE_SUBAREA) {
          return this.getStepForCoupleSubarea(step.id);
        }

        if (answerId?.id === AnswersIds.TELL_ME_MORE) {
          return this.getStepForTellMeMore(step.id);
        }

        if (answerId?.id === AnswersIds.USER_AGE) {
          if (this.onBoardingStore.answers[AnswersIds.THERAPY_TYPE] === TherapyType.MINOR) {
            if (parseInt(answerId.value) >= 14) {
              return step.id === MinorAgeAnswers.ADVICE;
            } else if (parseInt(answerId.value) < 14) {
              return step.id === MinorAgeAnswers.REJECT;
            }
          }
        }

        if (answerId?.id === AnswersIds.USER_GENDER) {
          if (step.id === "*") return true;

          return this.onBoardingStore.answers[AnswersIds.THERAPY_TYPE] === step.id;
        }

        if (this.onBoardingStore.currentStep === StepId.EMAIL_VERIFICATION) {
          return this.getStepForEmailVerification(step.id);
        }

        return step.id === "*" || step.id === answerId?.value;
      });

      if (availableSteps.length > 0 && availableSteps[0].nextStep) {
        this.onBoardingStore.historicalSteps.push(this.onBoardingStore.currentStep);
        this.onBoardingStore.currentStep = availableSteps[0].nextStep;
      }
    }
  }

  private getStepForWelcomeAgain(stepId: string): boolean {
    if (stepId === "*") return true;

    return stepId === WITHOUT_THERAPIST && !hasValue(this.userStore.getLastTherapist);
  }

  private getStepForUsername(stepId: string): boolean {
    if (stepId === "*") return true;

    return (
      stepId === WELLHUB_USER && (this.onBoardingStore.isWellhubUser || this.onBoardingStore.isGympassUser)
    );
  }

  private getStepGoToLastTherapist(stepId: string): boolean {
    const answer = this.onBoardingStore.answers[AnswersIds.GO_TO_LAST_THERAPIST];

    if (stepId === YesOrNoAnswer.YES && answer === YesOrNoAnswer.YES) return true;
    if (stepId === YesOrNoAnswer.NO && answer === YesOrNoAnswer.NO) return true;

    return (
      stepId === SUBSCRIPTION_ACTIVE_LAST_THERAPIST &&
      answer === YesOrNoAnswer.YES &&
      this.subscriptionStore.hasSubscription
    );
  }

  private getStepForTherapyType(stepId: string): boolean {
    if (stepId === "*") return true;
    if (stepId === TherapyType.COACHING && this.onBoardingStore.isCoachingSelected) return true;

    return stepId === B2B_USER && this.onBoardingStore.isB2bUser;
  }

  private getStepForMainArea(stepId: string): boolean {
    if (stepId === "*") return true;

    return this.onBoardingStore.isTherapistKnowSelected;
  }

  private getStepForCoupleSubarea(stepId: string): boolean {
    if (stepId === "*") return true;

    return this.onBoardingStore.isTherapistKnowSelected;
  }

  private getStepForTellMeMore(stepId: string): boolean {
    const hasSubscription = this.subscriptionStore.hasSubscription;

    if (stepId === "*") return true;
    if (
      stepId === SUBSCRIPTION_ACTIVE_THERAPIST_KNOWN &&
      hasSubscription &&
      this.onBoardingStore.isTherapistKnowSelected
    ) {
      return true;
    }

    return (
      stepId === AREA_EXTRA_THERAPIST_KNOW && this.onBoardingStore.isTherapistKnowSelected && !hasSubscription
    );
  }

  private getStepForRegisterUserComeBack(stepId: string): boolean {
    if (stepId === "*" && !this.subscriptionStore.hasSubscription) return true;

    return this.subscriptionStore.hasSubscription;
  }

  private getStepForEmailVerification(stepId: string): boolean {
    if (stepId === "*") return true;

    return stepId === CUSTOM_B2B_USER && this.onBoardingStore.isCustomB2bUser;
  }

  private getStepForCheckout(stepId: string): boolean {
    if (stepId === "*") return true;

    return this.onBoardingStore.isTherapistKnowSelected || !hasValue(this.userStore.getLastTherapist);
  }

  private getStepForZone(stepId: string): boolean {
    if (stepId === "*") return true;

    return stepId === OTHER_LOCATION && this.onBoardingStore.isOtherLocationSelected;
  }

  private getStepForReassignReason(stepId: string): boolean {
    if (stepId === "*") return true;

    const answer = this.onBoardingStore.answers[AnswersIds.REASSIGN_REASON];
    const reassignReason = this.onBoardingStore.getChoiceInfo(
      StepIdWithChoices.REASSIGN_REASON,
      answer as string,
      "uuid"
    );

    return stepId === reassignReason?.i18n;
  }
}

export default SetCurrentStepActionHandler;
